import React, { useState, useEffect } from 'react';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import './CustomerProfile.css';

const CustomerProfile = ({ isOpen, onClose }) => {
  const { customer, logout, requestPermission } = useCustomerAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchProfile();
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('customerToken');
      const response = await fetch('/api/customers/profile', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setFormData({
          firstName: data.first_name || '',
          lastName: data.last_name || '',
          company: data.company || '',
          phone: data.phone || '',
          address: data.address || '',
          city: data.city || '',
          state: data.state || '',
          country: data.country || '',
          postalCode: data.postal_code || ''
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('customerToken');
      const response = await fetch('/api/customers/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setMessage('Perfil actualizado exitosamente');
        setEditing(false);
        fetchProfile(); // Refresh profile data
      } else {
        const data = await response.json();
        setMessage(`Error: ${data.error}`);
      }
    } catch (error) {
      setMessage('Error al actualizar el perfil');
    } finally {
      setSaving(false);
    }
  };

  const handleRequestPermission = async () => {
    const result = await requestPermission('price_access', 'Solicito acceso a precios de productos');
    if (result.success) {
      setMessage('Solicitud enviada exitosamente');
    } else {
      setMessage(`Error: ${result.error}`);
    }
  };

  if (loading) {
    return (
      <div className="customer-profile-overlay">
        <div className="customer-profile-modal">
          <div className="loading">Cargando perfil...</div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="customer-profile-overlay">
        <div className="customer-profile-modal">
          <div className="error">Error al cargar el perfil</div>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-profile-overlay">
      <div className="customer-profile-modal">
        <div className="customer-profile-header">
          <h2>Mi Perfil</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="customer-profile-content">
          {message && (
            <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
              {message}
            </div>
          )}

          <div className="profile-section">
            <h3>Información Personal</h3>
            <div className="profile-info">
              <div className="info-item">
                <label>Email:</label>
                <span>{profile.email}</span>
              </div>
              <div className="info-item">
                <label>Estado de Permisos:</label>
                <span className={`status ${profile.permission_status}`}>
                  {profile.permission_status === 'approved' ? 'Aprobado' : 
                   profile.permission_status === 'pending' ? 'Pendiente' : 'Rechazado'}
                </span>
              </div>
              <div className="info-item">
                <label>Miembro desde:</label>
                <span>{new Date(profile.created_at).toLocaleDateString('es-MX')}</span>
              </div>
            </div>
          </div>

          <div className="profile-section">
            <div className="section-header">
              <h3>Información de Contacto</h3>
              <button 
                className="edit-btn"
                onClick={() => setEditing(!editing)}
              >
                {editing ? 'Cancelar' : 'Editar'}
              </button>
            </div>

            {editing ? (
              <div className="profile-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Nombre</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Apellido</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Empresa</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label>Teléfono</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label>Dirección</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    rows="3"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Ciudad</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Estado</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>País</label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Código Postal</label>
                    <input
                      type="text"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <button 
                  className="save-btn"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            ) : (
              <div className="profile-info">
                <div className="info-item">
                  <label>Nombre:</label>
                  <span>{profile.first_name} {profile.last_name}</span>
                </div>
                <div className="info-item">
                  <label>Empresa:</label>
                  <span>{profile.company || 'No especificada'}</span>
                </div>
                <div className="info-item">
                  <label>Teléfono:</label>
                  <span>{profile.phone || 'No especificado'}</span>
                </div>
                <div className="info-item">
                  <label>Dirección:</label>
                  <span>{profile.address || 'No especificada'}</span>
                </div>
                <div className="info-item">
                  <label>Ciudad:</label>
                  <span>{profile.city || 'No especificada'}</span>
                </div>
                <div className="info-item">
                  <label>Estado:</label>
                  <span>{profile.state || 'No especificado'}</span>
                </div>
                <div className="info-item">
                  <label>País:</label>
                  <span>{profile.country || 'No especificado'}</span>
                </div>
                <div className="info-item">
                  <label>Código Postal:</label>
                  <span>{profile.postal_code || 'No especificado'}</span>
                </div>
              </div>
            )}
          </div>

          {profile.permission_status !== 'approved' && (
            <div className="profile-section">
              <h3>Solicitar Acceso a Precios</h3>
              <p>Para ver los precios de los productos, necesitas que un administrador apruebe tu solicitud.</p>
              <button 
                className="request-btn"
                onClick={handleRequestPermission}
              >
                Solicitar Acceso a Precios
              </button>
            </div>
          )}

          <div className="profile-actions">
            <button className="logout-btn" onClick={logout}>
              Cerrar Sesión
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerProfile;
